import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { Checkbox } from '@heyroot/ui/primitives/Checkbox';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { ErrorState } from '@heyroot/ui/primitives/ErrorState';
import { Input } from '@heyroot/ui/primitives/Input';
import { Skeleton } from '@heyroot/ui/primitives/Skeleton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from '@heyroot/ui/primitives/Table';
import { Drawer } from '@heyroot/ui/primitives/Drawer';
import { TypedConfirm } from '../typed-confirm';
import { TenantPermissionPicker } from './tenant-permission-picker';
import {
  fetchTenantRoles,
  fetchTenantRole,
  createTenantRole,
  updateTenantRole,
  archiveTenantRole,
  queryKeys,
  AdminApiError,
  type TenantRoleRow,
} from '../../lib/api';

// Exemplar: docs/design-system/exemplars -- closest existing surface is
// apps/admin's own roles-list.tsx / role-detail.tsx pair, adapted here into
// one tab (list + create/edit drawer) rather than a separate route, since
// tenant roles are scoped to this one tenant rather than a top-level entity.

type DrawerState = { mode: 'create' } | { mode: 'edit'; roleId: string };

export function RolesTab({ tenantId, canWrite }: { tenantId: string; canWrite: boolean }) {
  const qc = useQueryClient();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<TenantRoleRow | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const rolesQuery = useQuery({
    queryKey: queryKeys.tenantRoles(tenantId, includeArchived),
    queryFn: () => fetchTenantRoles(tenantId, includeArchived),
  });

  const archiveMutation = useMutation({
    mutationFn: (vars: { roleId: string; expectedVersion: number; reason: string }) =>
      archiveTenantRole(tenantId, vars.roleId, vars.expectedVersion, vars.reason),
    onSuccess: () => {
      setArchiveTarget(null);
      setArchiveError(null);
      void qc.invalidateQueries({ queryKey: ['tenant-roles', tenantId] });
    },
    onError: (err) => {
      // Fix round 2 (I6): ROLE_IN_USE only exists on the ADMIN-roles surface
      // (a different route entirely) -- this route's real codes are
      // TENANT_ADMIN_FLOOR_BREACH, ROLE_VERSION_CONFLICT,
      // BUILT_IN_ROLE_IMMUTABLE and ROLE_NOT_FOUND, all with a genuinely
      // actionable server message (see admin-tenant-roles-routes.ts's
      // archive route) -- falling back to a generic string discarded that,
      // most importantly the floor-breach case's "grant ... to another user
      // first" guidance.
      setArchiveError(err instanceof AdminApiError ? err.message : 'Could not archive this role.');
    },
  });

  if (rolesQuery.isPending) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-[75%]" />
      </div>
    );
  }
  if (rolesQuery.isError) {
    return (
      <ErrorState title="Could not load tenant roles" onRetry={() => void rolesQuery.refetch()} />
    );
  }

  const roles = rolesQuery.data?.roles ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-body-sm text-secondary">
          <Checkbox
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
          />
          Include archived
        </label>
        {canWrite && (
          <Button variant="primary" onClick={() => setDrawer({ mode: 'create' })}>
            New role
          </Button>
        )}
      </div>

      {roles.length === 0 ? (
        <EmptyState
          title="No custom roles yet"
          description="Built-in roles are always available."
        />
      ) : (
        <div className="overflow-x-auto rounded-control border border-border">
          <Table dense>
            <TableHead>
              <tr>
                <TableHeaderCell>Role</TableHeaderCell>
                <TableHeaderCell>Permissions</TableHeaderCell>
                <TableHeaderCell>Assignees</TableHeaderCell>
                <TableHeaderCell />
              </tr>
            </TableHead>
            <TableBody>
              {roles.map((role) => (
                <TableRow key={role.id} className="hover:bg-surface-elevated transition-colors">
                  <TableCell className="min-w-0 truncate">
                    <div className="flex items-center gap-2">
                      <span className="text-primary">{role.name}</span>
                      {role.isBuiltIn && <Badge variant="neutral">Built-in</Badge>}
                      {role.isElevated && <Badge variant="warning">Elevated</Badge>}
                      {role.archivedAt && <Badge variant="neutral">Archived</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="tabular-nums">{role.permissionCount}</TableCell>
                  <TableCell className="tabular-nums">{role.userCount}</TableCell>
                  <TableCell>
                    {!role.isBuiltIn && (
                      <div className="flex gap-2">
                        {canWrite && (
                          <Button
                            variant="link"
                            onClick={() => setDrawer({ mode: 'edit', roleId: role.id })}
                          >
                            Edit
                          </Button>
                        )}
                        {canWrite && !role.archivedAt && (
                          <Button
                            variant="link"
                            className="text-danger-text"
                            onClick={() => {
                              setArchiveError(null);
                              setArchiveTarget(role);
                            }}
                          >
                            Archive
                          </Button>
                        )}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Drawer
        open={drawer !== null}
        onClose={() => setDrawer(null)}
        title={drawer?.mode === 'create' ? 'New role' : 'Edit role'}
        width="wide"
      >
        {drawer && (
          <RoleForm
            tenantId={tenantId}
            state={drawer}
            onDone={() => {
              setDrawer(null);
              void qc.invalidateQueries({ queryKey: ['tenant-roles', tenantId] });
            }}
            onCancel={() => setDrawer(null)}
          />
        )}
      </Drawer>

      {archiveTarget && (
        <TypedConfirm
          open={archiveTarget !== null}
          title="Archive role"
          body={
            (archiveError ? archiveError + ' ' : '') +
            `This permanently archives the "${archiveTarget.name}" role.`
          }
          confirmValue={archiveTarget.name}
          confirmLabel="Archive"
          requireReason
          pending={archiveMutation.isPending}
          onCancel={() => setArchiveTarget(null)}
          onConfirm={(reason) =>
            fetchTenantRole(tenantId, archiveTarget.id)
              .then(({ role }) =>
                archiveMutation.mutate({
                  roleId: archiveTarget.id,
                  expectedVersion: role.rowVersion,
                  reason,
                }),
              )
              // Fix round 2 (I6): this chain had no .catch -- a failed
              // refetch (before the mutation even runs) was a silent no-op
              // plus an unhandled promise rejection, not a message the
              // operator could see.
              .catch(() => setArchiveError('Could not archive this role. Reload and try again.'))
          }
        />
      )}
    </div>
  );
}

function RoleForm({
  tenantId,
  state,
  onDone,
  onCancel,
}: {
  tenantId: string;
  state: DrawerState;
  onDone: () => void;
  onCancel: () => void;
}) {
  const detailQuery = useQuery({
    queryKey: queryKeys.tenantRole(tenantId, state.mode === 'edit' ? state.roleId : ''),
    queryFn: () => fetchTenantRole(tenantId, state.mode === 'edit' ? state.roleId : ''),
    enabled: state.mode === 'edit',
  });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [permissions, setPermissions] = useState<string[]>([]);
  const [seeded, setSeeded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (state.mode === 'edit' && detailQuery.data && !seeded) {
    setName(detailQuery.data.role.name);
    setDescription(detailQuery.data.role.description ?? '');
    setPermissions(detailQuery.data.role.permissions);
    setSeeded(true);
  }

  const saveMutation = useMutation({
    mutationFn: () =>
      state.mode === 'create'
        ? createTenantRole(tenantId, { displayName: name, description, permissions })
        : updateTenantRole(tenantId, state.roleId, {
            displayName: name,
            description,
            permissions,
            expectedVersion: detailQuery.data!.role.rowVersion,
          }),
    onSuccess: onDone,
    onError: (err) => {
      setError(
        err instanceof AdminApiError
          ? ((
              {
                ROLE_NAME_TAKEN: 'A role with that name already exists.',
                PERMISSIONS_TOO_MANY: 'Too many permissions selected.',
                PERMISSIONS_DUPLICATED: 'One or more permissions were duplicated.',
                UNKNOWN_PERMISSION: 'One of the selected permissions no longer exists.',
                ROLE_VERSION_CONFLICT: 'This role changed elsewhere. Reload and try again.',
                BUILT_IN_ROLE_IMMUTABLE: 'Built-in roles cannot be edited.',
              } as Record<string, string>
            )[err.code] ?? err.message)
          : 'Could not save this role.',
      );
    },
  });

  if (state.mode === 'edit' && detailQuery.isPending) {
    return <Skeleton className="h-40 w-full" />;
  }
  if (state.mode === 'edit' && detailQuery.isError) {
    return <p className="text-body-sm text-danger-text">Could not load this role.</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <p role="alert" className="text-body-sm text-danger-text">
          {error}
        </p>
      )}
      <label className="flex flex-col gap-1.5">
        <span className="text-body-sm-emphasis text-secondary">Name</span>
        <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </label>
      <label className="flex flex-col gap-1.5">
        <span className="text-body-sm-emphasis text-secondary">Description</span>
        <Input value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <section>
        <h3 className="text-eyebrow uppercase text-muted mb-2">Permissions</h3>
        <TenantPermissionPicker selected={permissions} onChange={setPermissions} />
      </section>
      {/* Fix round 2 (I2): the server requires permissions.min(1) and returns
          a raw zod message ("Array must contain at least 1 element(s)") on
          violation -- this guard makes that path unreachable from the UI. */}
      {permissions.length === 0 && (
        <p className="text-body-sm text-muted">Select at least one permission.</p>
      )}
      <div className="flex gap-2">
        <Button
          variant="primary"
          disabled={!name.trim() || permissions.length === 0 || saveMutation.isPending}
          loading={saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          {state.mode === 'create' ? 'Create' : 'Save'}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
